import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationService } from '@alfresco/adf-core';
import { AppConfigService } from '@alfresco/adf-core';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface InvoiceRequest {
  invoiceNumber: string;
  invoiceDate: string;           // yyyy-MM-dd
  invoiceAmount: number;
  currency?: string;
  description?: string;
  supplierName?: string;
  poReference?: string;
  invoiceStatus?: 'PENDING' | 'APPROVED' | 'PAID' | 'REJECTED';
}

export interface InvoiceResponse {
  id: number;
  contractNodeId: string;
  contractNumber: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
  currency: string;
  invoiceStatus: 'PENDING' | 'APPROVED' | 'PAID' | 'REJECTED';
  description?: string;
  supplierName?: string;
  poReference?: string;
  hasDocument: boolean;
  invoiceNodeId?: string;
  invoiceFileName?: string;
  uploadedBy?: string;
  createdAt: string;
  updatedAt: string;
  daysOld: number;
}

export interface InvoiceSummary {
  contractAmount: number | null;
  totalInvoiced: number;
  totalPaid: number;
  remainingBalance: number;
  percentageInvoiced: number;
  invoiceCount: number;
  currency?: string;
  lastInvoiceDate?: string;
  contractAmountMissing: boolean;
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class ContractInvoiceService {

  //private baseUrl = 'http://localhost:9090/api/contracts';
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private appConfig: AppConfigService
  ) {
    const base = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.baseUrl = `${base}/contracts`;
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  private getAuthHeadersOnly(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({ 'Authorization': `Bearer ${token}` });
  }

  // ── List invoices ──────────────────────────────────────────────────────────

  getInvoices(contractNodeId: string): Observable<InvoiceResponse[]> {
    return this.http
      .get<any>(`${this.baseUrl}/${contractNodeId}/invoices`, { headers: this.getHeaders() })
      .pipe(
        map(r => r.invoices || []),
        catchError(this.handleError)
      );
  }

  // ── Summary ────────────────────────────────────────────────────────────────

  getInvoiceSummary(contractNodeId: string): Observable<InvoiceSummary> {
    return this.http
      .get<any>(`${this.baseUrl}/${contractNodeId}/invoices/summary`, { headers: this.getHeaders() })
      .pipe(
        map(r => r.summary),
        catchError(this.handleError)
      );
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  createInvoice(contractNodeId: string, request: InvoiceRequest): Observable<InvoiceResponse> {
    return this.http
      .post<any>(`${this.baseUrl}/${contractNodeId}/invoices`, request, { headers: this.getHeaders() })
      .pipe(
        map(r => r.invoice),
        catchError(this.handleError)
      );
  }

  // ── Attach document ────────────────────────────────────────────────────────

  attachDocument(contractNodeId: string, invoiceId: number, file: File): Observable<InvoiceResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<any>(
        `${this.baseUrl}/${contractNodeId}/invoices/${invoiceId}/attach`,
        formData,
        { headers: this.getAuthHeadersOnly() }
      )
      .pipe(
        map(r => r.invoice),
        catchError(this.handleError)
      );
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  updateInvoice(contractNodeId: string, invoiceId: number, request: InvoiceRequest): Observable<InvoiceResponse> {
    return this.http
      .put<any>(
        `${this.baseUrl}/${contractNodeId}/invoices/${invoiceId}`,
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map(r => r.invoice),
        catchError(this.handleError)
      );
  }

  // ── Status change ──────────────────────────────────────────────────────────

  updateInvoiceStatus(
    contractNodeId: string,
    invoiceId: number,
    status: 'PENDING' | 'APPROVED' | 'PAID' | 'REJECTED'
  ): Observable<InvoiceResponse> {
    const params = new HttpParams().set('status', status);
    return this.http
      .put<any>(
        `${this.baseUrl}/${contractNodeId}/invoices/${invoiceId}/status`,
        null,
        { headers: this.getHeaders(), params }
      )
      .pipe(
        map(r => r.invoice),
        catchError(this.handleError)
      );
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  deleteInvoice(contractNodeId: string, invoiceId: number): Observable<void> {
    return this.http
      .delete<void>(
        `${this.baseUrl}/${contractNodeId}/invoices/${invoiceId}`,
        { headers: this.getHeaders() }
      )
      .pipe(catchError(this.handleError));
  }

  // ── Download document ──────────────────────────────────────────────────────

  downloadInvoiceDocument(contractNodeId: string, invoiceId: number): Observable<Blob> {
    return this.http
      .get(
        `${this.baseUrl}/${contractNodeId}/invoices/${invoiceId}/download`,
        { headers: this.getAuthHeadersOnly(), responseType: 'blob' }
      )
      .pipe(catchError(this.handleError));
  }

  // ── Utility ────────────────────────────────────────────────────────────────

  downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  private handleError(error: any): Observable<never> {
    console.error('ContractInvoiceService Error:', error);
    const msg = error?.error?.error || error?.message || 'An error occurred';
    return throwError(() => new Error(msg));
  }
}
